import { useEffect, useMemo, useState } from "react";
import { Table, Spinner, Button, Input, Alert } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { saveAs } from "file-saver";
import { getPortfolio, exportPortfolioExcel } from "./TenantForm/Helpers/api";
import "./portfolioTable.css";

const money = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(n) || 0);

/** Compact money for the summary tiles — $1.2M / $48.3k / $920. */
const moneyShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
};

const day = (v) => (v ? String(v).slice(0, 10) : "—");

const DAY_MS = 86400000;

/** Whole days from today to `v`; null when there is no usable date. */
const daysUntil = (v) => {
  if (!v) return null;
  const t = new Date(String(v).slice(0, 10)).getTime();
  if (!Number.isFinite(t)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((t - today.getTime()) / DAY_MS);
};

/**
 * The same three variance bands the per-property sheet paints: at/above target,
 * short by under $75, short by $75 or more.
 */
const varianceOf = (row) => {
  const basic = Number(row.basic_rent);
  const target = Number(row.target_rent);
  if (!Number.isFinite(basic) || !Number.isFinite(target) || (!basic && !target)) {
    return { diff: null, band: null };
  }
  const diff = target - basic;
  if (diff <= 0) return { diff, band: "up", cls: "pft-var--up", indicator: "▲", label: `+${money(Math.abs(diff))}` };
  if (diff < 75) return { diff, band: "near", cls: "pft-var--near", indicator: "~", label: `-${money(diff)}` };
  return { diff, band: "down", cls: "pft-var--down", indicator: "▼", label: `-${money(diff)}` };
};

const isOccupied = (r) => !!String(r.tenant || "").trim();

const PAGE_SIZE = 100;

const LEASE_FILTERS = [
  { value: "", label: "Any lease" },
  { value: "active", label: "Active lease" },
  { value: "none", label: "No active lease" },
  { value: "30", label: "Ends ≤ 30d" },
  { value: "60", label: "Ends ≤ 60d" },
  { value: "90", label: "Ends ≤ 90d" },
  { value: "expired", label: "Already ended" },
];

/** Columns, in render order. `sort` is the row accessor sorting uses. */
const COLUMNS = [
  { key: "property_name", label: "Property", cls: "pft-c-prop", sort: (r) => r.property_name || "" },
  { key: "unit_no", label: "Unit", cls: "pft-c-unit", sort: (r) => r.unit_no || "" },
  { key: "building_name", label: "Building", sort: (r) => r.building_name || "" },
  { key: "tenant", label: "Tenant", sort: (r) => r.tenant || "" },
  { key: "status", label: "Status", sort: (r) => r.status || "" },
  { key: "sqft", label: "Sq Ft", cls: "pft-num", sort: (r) => Number(r.sqft) || 0 },
  { key: "basic_rent", label: "Basic Rent", cls: "pft-num", sort: (r) => Number(r.basic_rent) || 0 },
  { key: "target_rent", label: "Target", cls: "pft-num", sort: (r) => Number(r.target_rent) || 0 },
  { key: "variance", label: "Variance", cls: "pft-var", sort: (r) => varianceOf(r).diff ?? Number.NEGATIVE_INFINITY },
  { key: "sd", label: "SD", cls: "pft-num", sort: (r) => Number(r.sd) || 0 },
  { key: "lease_start", label: "Lease Start", sort: (r) => r.lease_start || "" },
  { key: "lease_end", label: "Lease End", sort: (r) => r.lease_end || "" },
  { key: "insurance_expiry", label: "Insurance", sort: (r) => r.insurance_expiry || "" },
  { key: "lease_state", label: "Lease State", sort: (r) => (r.has_active_lease ? r.lease_state || "Active" : "") },
];

/** A date cell that turns amber inside 60 days and red once it has passed. */
const DateCell = ({ value, warnWithin = 60 }) => {
  if (!value) return <span className="pft-muted">—</span>;
  const d = daysUntil(value);
  const cls = d === null ? "" : d < 0 ? "pft-date--past" : d <= warnWithin ? "pft-date--soon" : "";
  return (
    <span className={cls}>
      {day(value)}
      {d !== null && d < 0 && <span className="pft-sub">expired {Math.abs(d)}d ago</span>}
      {d !== null && d >= 0 && d <= warnWithin && <span className="pft-sub">in {d}d</span>}
    </span>
  );
};

/**
 * The "All Properties" mastersheet: every unit in the viewer's scope in one table of
 * the columns all sheets share, wearing the same navy/amber/green skin as a single
 * property's sheet. Clicking a row drops into that property's full sheet (its own
 * columns, editing, imports — everything as today). Filtering and sorting are
 * client-side: the whole portfolio is already in memory.
 */
const PortfolioTable = ({ onOpenProperty, onOpenReport }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [search, setSearch] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [occupancyFilter, setOccupancyFilter] = useState("");
  const [leaseFilter, setLeaseFilter] = useState("");
  const [sort, setSort] = useState({ key: "property_name", dir: "asc" });
  const [page, setPage] = useState(1);

  useEffect(() => {
    let active = true;
    getPortfolio()
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load");
        setData(resp.data);
      })
      .catch((err) => active && setError(err?.response?.data?.message || err.message || "Failed to load the portfolio"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  // Stable identities: a fresh `[]` on every render would re-run every memo below.
  const rows = useMemo(() => data?.rows || [], [data]);
  const properties = useMemo(() => data?.properties || [], [data]);

  const statuses = useMemo(
    () => [...new Set(rows.map((r) => r.status).filter(Boolean))].sort(),
    [rows]
  );

  // Buildings narrow to the selected property so the dropdown never offers a
  // combination that yields nothing.
  const buildings = useMemo(() => {
    const scope = propertyFilter ? rows.filter((r) => r.property_id === propertyFilter) : rows;
    return [...new Set(scope.map((r) => r.building_name).filter(Boolean))].sort();
  }, [rows, propertyFilter]);

  useEffect(() => {
    if (buildingFilter && !buildings.includes(buildingFilter)) setBuildingFilter("");
  }, [buildings, buildingFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const days = parseInt(leaseFilter, 10);
    const horizon = Number.isFinite(days) && days > 0 ? new Date(Date.now() + days * DAY_MS) : null;
    const now = new Date();

    return rows.filter((r) => {
      if (propertyFilter && r.property_id !== propertyFilter) return false;
      if (buildingFilter && r.building_name !== buildingFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (occupancyFilter === "occupied" && !isOccupied(r)) return false;
      if (occupancyFilter === "vacant" && isOccupied(r)) return false;
      if (q && !`${r.unit_no} ${r.tenant} ${r.property_name} ${r.building_name} ${r.status} ${r.comments || ""}`.toLowerCase().includes(q)) return false;

      if (leaseFilter === "active" && !r.has_active_lease) return false;
      if (leaseFilter === "none" && r.has_active_lease) return false;
      if (leaseFilter === "expired") {
        const d = daysUntil(r.lease_end);
        if (d === null || d >= 0) return false;
      }
      if (horizon) {
        if (!r.lease_end) return false;
        const end = new Date(r.lease_end);
        if (isNaN(end.getTime()) || end > horizon || end < now) return false;
      }
      return true;
    });
  }, [rows, search, propertyFilter, buildingFilter, statusFilter, occupancyFilter, leaseFilter]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = col.sort(a);
      const bv = col.sort(b);
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      if (cmp !== 0) return cmp * dir;
      // Stable tiebreak so equal keys still read as a sheet: property, then unit.
      return (
        String(a.property_name).localeCompare(String(b.property_name)) ||
        String(a.unit_no).localeCompare(String(b.unit_no), undefined, { numeric: true })
      );
    });
  }, [filtered, sort]);

  // Everything the tiles and the totals row show, over the *filtered* set —
  // narrowing the view narrows the numbers, exactly like the per-property sheet.
  const totals = useMemo(() => {
    const t = { units: sorted.length, occupied: 0, basic: 0, target: 0, sd: 0, sqft: 0, up: 0, near: 0, down: 0, expiring: 0 };
    for (const r of sorted) {
      if (isOccupied(r)) t.occupied += 1;
      t.basic += Number(r.basic_rent) || 0;
      t.target += Number(r.target_rent) || 0;
      t.sd += Number(r.sd) || 0;
      t.sqft += Number(r.sqft) || 0;
      const { band } = varianceOf(r);
      if (band === "up") t.up += 1;
      else if (band === "near") t.near += 1;
      else if (band === "down") t.down += 1;
      const d = daysUntil(r.lease_end);
      if (d !== null && d >= 0 && d <= 60) t.expiring += 1;
    }
    t.vacant = t.units - t.occupied;
    t.gap = t.target - t.basic;
    t.occupancy = t.units ? Math.round((t.occupied / t.units) * 100) : 0;
    return t;
  }, [sorted]);

  const hasFilters = !!(search || propertyFilter || buildingFilter || statusFilter || occupancyFilter || leaseFilter);

  const clearFilters = () => {
    setSearch("");
    setPropertyFilter("");
    setBuildingFilter("");
    setStatusFilter("");
    setOccupancyFilter("");
    setLeaseFilter("");
  };

  useEffect(() => { setPage(1); }, [search, propertyFilter, buildingFilter, statusFilter, occupancyFilter, leaseFilter, sort]);

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportPortfolioExcel();
      saveAs(new Blob([blob]), "Portfolio_MasterSheet.xlsx");
    } catch (e) {
      console.error("Portfolio export failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" size="lg" className="mb-3" />
        <h5 className="text-muted">Loading all properties…</h5>
      </div>
    );
  }
  if (error) return <Alert color="danger" className="m-3">{error}</Alert>;

  return (
    <div className="pft">
      <div className="pft-toolbar">
        <div className="pft-search">
          <FeatherIcon icon="search" size={14} />
          <Input
            bsSize="sm"
            placeholder="Search unit, tenant, property…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search portfolio"
          />
        </div>
        <Input type="select" bsSize="sm" className="pft-field" value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)} aria-label="Filter by property">
          <option value="">All properties</option>
          {properties.map((p) => (
            <option key={p.property_id} value={p.property_id}>{p.property_name}</option>
          ))}
        </Input>
        <Input type="select" bsSize="sm" className="pft-field" value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)} aria-label="Filter by building" disabled={buildings.length === 0}>
          <option value="">All buildings</option>
          {buildings.map((b) => <option key={b} value={b}>{b}</option>)}
        </Input>
        <Input type="select" bsSize="sm" className="pft-field" value={occupancyFilter} onChange={(e) => setOccupancyFilter(e.target.value)} aria-label="Filter by occupancy">
          <option value="">All units</option>
          <option value="occupied">Occupied</option>
          <option value="vacant">Vacant</option>
        </Input>
        <Input type="select" bsSize="sm" className="pft-field" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
          <option value="">Any status</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </Input>
        <Input type="select" bsSize="sm" className="pft-field" value={leaseFilter} onChange={(e) => setLeaseFilter(e.target.value)} aria-label="Filter by lease">
          {LEASE_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Input>
        {hasFilters && (
          <button type="button" className="pft-clear" onClick={clearFilters}>
            <FeatherIcon icon="x" size={12} className="me-1" />Clear
          </button>
        )}
        <span className="pft-actions">
          <Button size="sm" color="success" onClick={handleExport} disabled={downloading}>
            {downloading ? <Spinner size="sm" /> : <><FeatherIcon icon="download" size={13} className="me-1" /> Export</>}
          </Button>
          <Button size="sm" color="primary" onClick={onOpenReport}>
            <FeatherIcon icon="bar-chart-2" size={13} className="me-1" /> Report
          </Button>
        </span>
      </div>

      {rows.length === 0 ? (
        <Alert color="warning" className="text-center">No mastersheet units found for the properties assigned to you.</Alert>
      ) : (
        <>
          <div className="pft-stats">
            <div className="pft-stat">
              <div className="pft-stat__label">Units</div>
              <div className="pft-stat__value">
                {filtered.length === rows.length
                  ? `${rows.length} units · ${properties.length} propert${properties.length === 1 ? "y" : "ies"}`
                  : `${filtered.length} of ${rows.length} units`}
              </div>
            </div>
            <div className="pft-stat pft-stat--occupied">
              <div className="pft-stat__label">Occupied</div>
              <div className="pft-stat__value">{totals.occupied}</div>
              <div className="pft-stat__sub">{totals.occupancy}% occupancy</div>
            </div>
            <div className="pft-stat pft-stat--vacant">
              <div className="pft-stat__label">Vacant</div>
              <div className="pft-stat__value">{totals.vacant}</div>
              <div className="pft-stat__sub">{totals.expiring} lease{totals.expiring === 1 ? "" : "s"} ending ≤60d</div>
            </div>
            <div className="pft-stat pft-stat--rent">
              <div className="pft-stat__label">Basic Rent</div>
              <div className="pft-stat__value">{moneyShort(totals.basic)}</div>
              <div className="pft-stat__sub">per month</div>
            </div>
            <div className="pft-stat">
              <div className="pft-stat__label">Target Rent</div>
              <div className="pft-stat__value">{moneyShort(totals.target)}</div>
              <div className="pft-stat__sub">per month</div>
            </div>
            <div className="pft-stat pft-stat--gap">
              <div className="pft-stat__label">Gap to Target</div>
              <div className="pft-stat__value">{moneyShort(totals.gap)}</div>
              <div className="pft-stat__sub">▲{totals.up} ~{totals.near} ▼{totals.down}</div>
            </div>
          </div>

          <div className="pft-scroll">
            <Table className="pft-table mb-0">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`${c.cls || ""} ${sort.key === c.key ? "is-sorted" : ""}`}
                      onClick={() => toggleSort(c.key)}
                      title={`Sort by ${c.label}`}
                    >
                      {c.label}
                      <span className="pft-sort-caret">
                        {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr style={{ cursor: "default" }}>
                    <td colSpan={COLUMNS.length}>
                      <div className="pft-empty">
                        <FeatherIcon icon="filter" size={26} />
                        <div className="pft-empty__title">No units match these filters</div>
                        <div>Clear a filter to widen the view.</div>
                      </div>
                    </td>
                  </tr>
                ) : pageRows.map((r) => {
                  const occupied = isOccupied(r);
                  const v = varianceOf(r);
                  return (
                    <tr
                      key={r.item_id}
                      className={occupied ? "" : "pft-vacant"}
                      title="Open this property's full sheet"
                      onClick={() => onOpenProperty && onOpenProperty(r.property_id, r.building_name)}
                    >
                      <td className="pft-c-prop" title={r.property_name}>{r.property_name}</td>
                      <td className="pft-c-unit">
                        <span className={`pft-dot ${occupied ? "pft-dot--on" : "pft-dot--off"}`} />
                        {r.unit_no}
                      </td>
                      <td>{r.building_name || <span className="pft-muted">—</span>}</td>
                      <td>{occupied ? r.tenant : <em className="pft-muted">vacant</em>}</td>
                      <td>{r.status ? <span className="pft-pill pft-pill--status">{r.status}</span> : <span className="pft-muted">—</span>}</td>
                      <td className="pft-num">{r.sqft ? Number(r.sqft).toLocaleString("en-CA") : <span className="pft-muted">—</span>}</td>
                      <td className="pft-num">{money(r.basic_rent)}</td>
                      <td className="pft-num">{money(r.target_rent)}</td>
                      <td className={`pft-var ${v.cls || ""}`}>
                        {v.band ? `${v.indicator} ${v.label}` : <span className="pft-muted">—</span>}
                      </td>
                      <td className="pft-num">{money(r.sd)}</td>
                      <td><DateCell value={r.lease_start} warnWithin={-1} /></td>
                      <td><DateCell value={r.lease_end} /></td>
                      <td><DateCell value={r.insurance_expiry} /></td>
                      <td>
                        <span className={`pft-pill ${r.has_active_lease ? "pft-pill--on" : "pft-pill--off"}`}>
                          {r.has_active_lease ? r.lease_state || "Active" : "None"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {sorted.length > 0 && (
                <tfoot>
                  <tr>
                    <td className="pft-c-prop">{totals.units} unit{totals.units === 1 ? "" : "s"}</td>
                    <td className="pft-c-unit">—</td>
                    <td>—</td>
                    <td>{totals.occupied} occupied</td>
                    <td>{totals.vacant} vacant</td>
                    <td className="pft-num">{totals.sqft ? totals.sqft.toLocaleString("en-CA") : "—"}</td>
                    <td className="pft-num">{money(totals.basic)}</td>
                    <td className="pft-num">{money(totals.target)}</td>
                    <td className="pft-var" style={{ fontSize: "0.85em" }}>
                      <span style={{ color: "#155724", marginRight: 6 }}>▲ {totals.up}</span>
                      <span style={{ color: "#004085", marginRight: 6 }}>~ {totals.near}</span>
                      <span style={{ color: "#721c24" }}>▼ {totals.down}</span>
                    </td>
                    <td className="pft-num">{money(totals.sd)}</td>
                    <td>—</td>
                    <td>{totals.expiring} ending ≤60d</td>
                    <td>—</td>
                    <td>—</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>

          {/* ── Variance legend — the same bands as the per-property sheet ── */}
          <div className="pft-legend">
            <b>Variance to Target:</b>
            <span><span className="pft-swatch" style={{ background: "#d4edda", border: "1px solid #c3e6cb" }} />▲ At / Above Target</span>
            <span><span className="pft-swatch" style={{ background: "#cce5ff", border: "1px solid #b8daff" }} />~ Below &lt; $75</span>
            <span><span className="pft-swatch" style={{ background: "#f8d7da", border: "1px solid #f5c6cb" }} />▼ Below ≥ $75</span>
            <span><span className="pft-swatch" style={{ background: "#fdf0f1", border: "1px solid #f5c6cb" }} />Vacant unit</span>
          </div>

          {pages > 1 && (
            <div className="pft-pager">
              <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</Button>
              Page {page} of {pages}
              <Button size="sm" color="light" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PortfolioTable;
